import { useDeleteMessage } from "@/components/messages/hooks/useDeleteMessage.tsx";
import { useSessionChat } from "@/components/messages/hooks/useSendMessage.tsx";
import type { AttachedFile } from "@/components/messages/Omnibox/types.ts";
import { useCanvasActionContext } from "@/components/canvas/canvasContext.tsx";
import { useCurrentUserContext } from "@/hooks/useUser.tsx";
import { apiErrorMessage, apiErrorReason } from "@/lib/http.ts";
import { userMessageTypes } from "@/types/messages.ts";
import type {
  GetEventListResp,
  GetMessageListResp,
  MessageThreadEvent,
  NewMessageReq,
  ThreadEventItem,
} from "@/types/threadEvents.ts";
import {
  messageQueries,
  THREAD_EVENT_PAGE_SIZE,
  THREAD_MESSAGE_PAGE_SIZE,
} from "@/shared/messageQueries.ts";
import { infiniteQueryOptions, useInfiniteQuery } from "@tanstack/react-query";
import * as ld from "lodash-es";
import { useCallback, useMemo, useRef, useState } from "react";
import { pairToolCallApprovalsInFeed } from "@/shared/toolCallApproval.ts";

/**
 * Why the most recent send failed. `reason` is the server's structured
 * discriminator when it sent one (e.g. `UNITS_EXHAUSTED_REASON`), so the
 * composer can render an actionable affordance instead of a bare string.
 */
export type SendError = { message: string; reason?: string };

export type SendMessageFn = (
  content: string,
  options?: {
    type?: NewMessageReq["type"];
    fileIds?: NewMessageReq["fileIds"];
    /**
     * Raw attachments to upload in the background after the optimistic message
     * shows. The mutation uploads these and POSTs; prefer this over pre-uploaded
     * `fileIds` for the in-thread send so the message + chips render instantly.
     */
    attachedFiles?: AttachedFile[];
    /** Structured agent context (e.g. time-range enrichment JSON) — forwarded to the server. */
    agentContent?: NewMessageReq["agentContent"];
    /** Compact calendar-selection descriptor; server expands into agentContent. */
    calendarContext?: NewMessageReq["calendarContext"];
    /** On-screen view-context snapshot (canvas top + calendar window) to forward to the server. */
    viewContext?: NewMessageReq["viewContext"];
  },
) => Promise<boolean>;

/**
 * Both views of a thread's debug data, so the viewer can toggle without re-plumbing props.
 * `events` is optional — legacy blobs only carry the messages view.
 */
export type DebugDataByView = {
  messages: GetMessageListResp;
  events?: GetEventListResp;
};

/**
 * Admin tool — echo back pre-loaded debug data. Used when debug data is passed in directly
 * (e.g. from a debug data download) rather than fetched from the API.
 *
 * The viewer passes both views in one object; the active view is selected by key here so
 * toggling in the UI doesn't require re-plumbing the prop.
 *
 * Web-only, so it stays out of the shared factory — and off the `["messages"]`
 * root, so no feed invalidation reaches it.
 */
const debugDataQuery = (threadId: number, debugData: DebugDataByView, isEventView: boolean) =>
  infiniteQueryOptions({
    queryKey: ["events", threadId, debugData, isEventView] as const,
    queryFn: async ({
      pageParam: _pageParam,
      queryKey,
    }): Promise<GetEventListResp | GetMessageListResp> => {
      const [, , data, eventView] = queryKey;
      return eventView && data.events ? data.events : data.messages;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: () => undefined,
    getPreviousPageParam: () => undefined,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    // The payload is part of the key, so a caller that re-reads a moving thread
    // gets a new entry per change — each one a full trace, hundreds of
    // kilobytes, retained for the default five minutes after it is superseded.
    // Nothing is lost by dropping them: this query only echoes back an object
    // the caller already holds, so a miss costs nothing to serve again.
    gcTime: 0,
  });

/**
 * Handles the state management for and networking for the message list view model.
 */
export const useMessageList = ({
  threadId,
  debugData,
  isEventView = false,
}: {
  threadId: number;
  debugData?: DebugDataByView;
  isEventView?: boolean;
}) => {
  const limit = isEventView ? THREAD_EVENT_PAGE_SIZE : THREAD_MESSAGE_PAGE_SIZE;
  // Query options for infinite scroll (historical messages)
  const queryOptions = useMemo(() => {
    if (debugData) {
      return debugDataQuery(threadId, debugData, isEventView);
    }
    if (isEventView) {
      return messageQueries.eventList(threadId, limit);
    }
    return messageQueries.list(threadId, limit);
  }, [debugData, isEventView, threadId, limit]);

  // Get user timezone for client context
  const { getUserTimezone, getUserLocation } = useCurrentUserContext();
  // The surrounding canvas's snapshot getter, if any — supplies the view-context
  // snapshot sent with each message. Depend on the getter itself (a stable
  // useCallback), not the whole canvas handle, so `sendMessage` keeps a stable
  // identity even if the handle's identity churns — otherwise MessageItem's memo
  // would break and re-parse every message's markdown. Undefined off a canvas.
  const getCanvasContentSnapshot = useCanvasActionContext()?.getSnapshot;
  // Cast needed because useInfiniteQuery can't accept the union of query option types directly.
  // We widen to ThreadEventItem (which MessageThreadEvent is a member of) so the flattened
  // pages below are always ThreadEventItem[].
  const { data, isPending, error, fetchPreviousPage, hasPreviousPage, isFetchingPreviousPage } =
    useInfiniteQuery(queryOptions as ReturnType<typeof messageQueries.eventList>);

  // Session chat hook manages WebSocket integration and optimistic updates
  const { sessionEvents, sendMutation, removeSessionEvent, addOptimisticUserMessage } =
    useSessionChat(threadId, isEventView);

  // ---------------------------------------------------------------------------
  // Build `items` — a single sorted, deduped array.
  // Combine infinite scroll (historical) items with session chat messages (live updates while page is open,
  // from both in session chat and "out of band" updates).
  //
  // Session messages are MessageThreadEvent (a member of ThreadEventItem), so they merge naturally
  // into both messages and events views. Session messages take precedence in dedup.
  //
  // Queued user messages are split off into `queuedUserItems` and rendered in
  // the QueuedMessageStack above the OmniBox instead of inline in the feed.
  // Once dequeued (status flips to `completed`, sortDate is bumped), they
  // reappear in `items` at their new sortDate.
  // ---------------------------------------------------------------------------
  const { items, queuedUserItems } = useMemo((): {
    items: ThreadEventItem[];
    queuedUserItems: MessageThreadEvent[];
  } => {
    // Flatten the loaded pages here rather than via the query's `select`: the
    // shared factory returns raw InfiniteData so both platforms can dedupe and
    // sort their own way. Structural sharing keeps `data` referentially stable
    // across a no-op refetch, so this memo doesn't recompute for one.
    const rawItems = data?.pages.flatMap((page) => page?.items ?? []) ?? [];

    // Put session messages first to ensure they take precedence in deduplication below
    const allItems: ThreadEventItem[] = [...Array.from(sessionEvents.values()), ...rawItems];

    // Deduplicate by event ID, prioritizing session items (first in array).
    // This handles the case where events appear in both sources (in which case, "historical" events
    // in the infinite scroll have since been updated, so we need the newer version from the session state).
    const deduplicatedItems = ld.uniqBy(allItems, (item) => item.id);

    const isQueuedUserMessage = (item: ThreadEventItem): item is MessageThreadEvent =>
      item.type === "message" &&
      item.message.status === "queued" &&
      // Idle-thread sends are stored `queued` but shown in the feed, not the
      // pill stack (see useSendMessage). The flag rides through reconciliation.
      !item.clientOnly?.suppressQueuedPill &&
      // A reaction waits for the agent like any user message, but it is drawn
      // as a badge on the reply it was put on, never as a pending message.
      item.message.type !== "autoReaction" &&
      userMessageTypes.safeParse(item.message.type).success;

    const queued = deduplicatedItems.filter(isQueuedUserMessage);
    const feed = deduplicatedItems.filter((item) => !isQueuedUserMessage(item));

    // Sort by sortDate to maintain chronological order (sortDate may differ from createdAt for finalized content).
    // Use id as secondary sort to ensure stable ordering when sortDate values are equal.
    return {
      // Paired here, where every surface reads the feed from, so the queue, the
      // inline cards and the canvas all see one tool-call card with buttons.
      items: pairToolCallApprovalsInFeed(ld.sortBy(feed, [(e) => e.sortDate, (e) => e.id])),
      // Queued messages render top-to-bottom = oldest-to-newest so the newest
      // sits closest to the OmniBox. Sort by sortDate (creation time).
      queuedUserItems: ld.sortBy(queued, [(e) => e.sortDate, (e) => e.id]),
    };
  }, [data, sessionEvents]);

  // wrapper for fetchPreviousPage - handles infinite scroll up
  const loadMore = useCallback(() => {
    // sendMutation.isPending - any in flight requests get cancelled during this mutation, but we want to check this here to ensure
    // we don't add another one - keeps thing simpler/less janky
    if (!error && !sendMutation.isPending) {
      fetchPreviousPage();
    }
  }, [error, fetchPreviousPage, sendMutation.isPending]);

  // Synchronous guard against double-sends (e.g. rapid clicks before React re-renders).
  const sendInFlightRef = useRef(false);

  // Why the failure reason lives here rather than in the caller: `SendMessageFn`
  // returns a boolean and is handed to the artifact components as well as the
  // composer, so widening it would ripple through callers that have no use for
  // the message. Owning the state here instead means every send surface reports
  // a failure, not just the one that happens to wrap the call.
  const [sendError, setSendError] = useState<SendError | null>(null);

  const sendMessage: SendMessageFn = useCallback(
    async (text, options = {}) => {
      // Cleared ahead of the in-flight guard so a suppressed double-send can
      // never leave the previous attempt's reason on screen.
      setSendError(null);
      if (sendInFlightRef.current) return false;
      sendInFlightRef.current = true;
      try {
        // The on-screen snapshot at submit time. `undefined` off a canvas
        // surface; the server re-filters, so it's a best-effort deixis hint.
        const viewContext = getCanvasContentSnapshot?.();
        const userLocation = getUserLocation();

        await sendMutation.mutateAsync({
          text,
          clientContext: {
            userTimezone: getUserTimezone(),
            userLocation: userLocation || undefined,
          },
          viewContext,
          ...options,
          type: options?.type ?? "userRequest",
        });
        return true;
      } catch (err) {
        console.error("Failed to send message:", err);
        // The server's own reason names something the user can act on, where the
        // generic prompt only invites the same failing retry. A network failure
        // carries no reason, so it keeps the generic wording.
        setSendError({
          message: apiErrorMessage(err) ?? "Failed to send. Please try again.",
          reason: apiErrorReason(err),
        });
        return false;
      } finally {
        sendInFlightRef.current = false;
      }
    },
    [sendMutation.mutateAsync, getUserTimezone, getUserLocation, getCanvasContentSnapshot],
  );

  const { deleteMessages, isDeleting } = useDeleteMessage({
    threadId,
    messageListQueryKey: queryOptions.queryKey,
  });

  return {
    items,
    queuedUserItems,
    removeSessionEvent,
    addOptimisticUserMessage,
    isFetchingPreviousPage,
    isPending,
    isSending: sendMutation.isPending,
    error,
    sendMessage,
    /** Why the most recent `sendMessage` failed, or `null` if none has. */
    sendError,
    hasPreviousPage,
    loadMore,
    deleteMessages,
    isDeleting,
  };
};
